import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Calendar, Icon, Input } from "@elaniintc/goes-design-system";
import { useState, useEffect } from "react";
import {
  type MaskitoDateParams,
  maskitoDateOptionsGenerator,
  maskitoStringifyDate,
  maskitoParseDate,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import dayjs from "dayjs";
import { cn } from "@/utils/theme";

const params: MaskitoDateParams = {
  mode: "dd/mm/yyyy",
  separator: "/",
};
const options = maskitoDateOptionsGenerator(params);

const MIN_DATE = new Date(1962, 0, 1); // 01/01/1962
const MAX_DATE = new Date(2055, 11, 31); // 31/12/2055

function clampDate(d: Date) {
  if (d < MIN_DATE) return MIN_DATE;
  if (d > MAX_DATE) return MAX_DATE;
  return d;
}

function fmt(date?: Date) {
  return date
    ? maskitoStringifyDate(date, { separator: "/", mode: "dd/mm/yyyy" })
    : "";
}

interface DateInputProps {
  onChangeDate: (date: string) => void;
  value?: string;
  placeholder?: string;
  group?:
    | "down"
    | "down-left"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left"
    | "middle-up";
  className?: string;
  wrapperClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  /** When true, shows a "Fecha actual" option at the bottom of the calendar */
  allowNow?: boolean;
  id?: string;
}

export const DateInput = ({
  onChangeDate,
  value,
  group,
  placeholder = "Seleccionar fecha",
  className,
  wrapperClassName,
  minDate = MIN_DATE,
  maxDate = MAX_DATE,
  disabled = false,
  allowNow = false,
  id,
}: DateInputProps) => {
  const isNow = value === "now";
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [dateValue, setDateValue] = useState<string>(
    isNow ? "Fecha actual" : "",
  );

  const maskedInputRef = useMaskito({ options });

  useEffect(() => {
    if (value === "now") {
      setDate(undefined);
      setDateValue("Fecha actual");
      return;
    }
    if (value) {
      const parsed = dayjs(value, "DD/MM/YYYY", true).toDate();
      if (!Number.isNaN(parsed.getTime())) {
        const safe = clampDate(parsed);
        setDate(safe);
        setDateValue(fmt(safe));
        if (safe.getTime() !== parsed.getTime()) {
          onChangeDate(safe.toISOString());
        }
        return;
      }
    }
    setDate(undefined);
    setDateValue("");
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cn(
        "relative w-full",
        wrapperClassName,
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      <Input
        id={id}
        ref={maskedInputRef}
        className={cn(
          "w-full border border-neutral bg-neutral-object-accent-silent",
          isNow && "text-action font-medium",
          className,
        )}
        value={dateValue}
        group={group}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={isNow}
        onFocus={() => {
          if (isNow) setOpen(true);
        }}
        onBlur={() => {
          if (isNow) return;
          const parsed = maskitoParseDate(dateValue, params);

          if (!parsed) {
            if (date) {
              setDateValue(fmt(date));
            } else {
              setDateValue("");
              onChangeDate("");
            }
            return;
          }

          const safe = clampDate(parsed);
          setDate(safe);
          setDateValue(fmt(safe));
          onChangeDate(safe.toISOString());
        }}
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = maskitoParseDate(raw, params);

          if (!parsed && raw.length === 0) {
            setDate(undefined);
            setDateValue("");
            onChangeDate("");
            return;
          }

          if (!parsed) {
            setDateValue(raw);
            return;
          }

          if (parsed < MIN_DATE || parsed > MAX_DATE) {
            if (date) {
              setDateValue(fmt(date));
            } else {
              setDateValue("");
            }
            return;
          }

          setDate(parsed);
          setDateValue(raw);
          onChangeDate(parsed.toISOString());
        }}
      />

      <div className="top-1.5 right-2 absolute flex items-center gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Seleccionar fecha">
              <Icon type="far" name="calendar" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-full overflow-hidden p-0 bg-neutral-object border-neutral"
            align="end"
            data-testid="date-input-popover"
          >
            <Calendar
              mode="single"
              selected={isNow ? undefined : date}
              captionLayout="dropdown"
              defaultMonth={date ? clampDate(date) : new Date()}
              startMonth={minDate}
              endMonth={maxDate}
              disabled={(d: Date) => d < minDate || d > maxDate}
              className="bg-transparent w-full"
              onSelect={(d?: Date) => {
                if (!d) return;
                const safe = clampDate(d);
                setDate(safe);
                setDateValue(fmt(safe));
                onChangeDate(safe.toISOString());
                setOpen(false);
              }}
            />
            {allowNow && (
              <div className="p-2 border-t border-neutral bg-neutral-object">
                <button
                  type="button"
                  className={cn(
                    "flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors",
                    isNow
                      ? "bg-action-silent text-action font-medium"
                      : "text-body hover:bg-neutral-object-accent-silent",
                  )}
                  onClick={() => {
                    if (isNow) {
                      setDate(undefined);
                      setDateValue("");
                      onChangeDate("");
                    } else {
                      setDate(undefined);
                      setDateValue("Fecha actual");
                      onChangeDate("now");
                    }
                    setOpen(false);
                  }}
                >
                  Fecha actual
                  {isNow && (
                    <Icon
                      type="fas"
                      name="check-circle"
                      size="lg"
                      className="text-action"
                    />
                  )}
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
